import {
  CustomFields,
  CustomObjectReference,
  FieldContainer,
  LineItem,
  LocalizedString,
  ProductVariant,
} from '@commercetools/platform-sdk';
import {
  INutsVariantAttributes as NutsVariantAttributes,
  pivotAttributeValues,
} from '@nuts/auto-delivery-sdk/dist/utils/helpers';
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import compact from 'lodash/compact';
import groupBy from 'lodash/groupBy';
import type { Except, SetRequired } from 'type-fest';

import { BYOB_PRODUCT_KEY } from '@/lib/customizers/boxes';
import { INGREDIENT_LIMITS, MIX_PRODUCT_KEY } from '@/lib/customizers/mixes';
import {
  LARGE_CUSTOM_TRAY_PRODUCT_KEY,
  MEDIUM_CUSTOM_TRAY_PRODUCT_KEY,
} from '@/lib/customizers/trays';
import { findAttributeByName } from '@/lib/product/attributes';
import { getProductDiscountDisplayValueFromPrice } from '@/lib/product/discounts/productDiscounts';
import { getListPrice, getMoneyValue } from '@/lib/product/prices';
import { isGiftLineItem } from '@/utils/cart';
import { DateString } from '@/utils/dateTime';
import { ImageBySize } from '@/utils/image';
import { isDefined } from '@/utils/isDefined';
import { computeSavings, LineSavingsBreakdownItem, LineSavingsSummary, Money } from '@/utils/money';
import { isBYOB } from '@/utils/product';
import { reportError } from '@/utils/reportError';

/** Technically each variant can define any valid price in CT; it is a business convention to set
 * these to $3.00. Performing a lookup for display purposes would be somewhat convoluted. In particular,
 * the ticket connected to this wants to display the CTA above the fold, and this seems better than
 * eagerly loading the greeting cards and finding the minimum price.
 */
export const GREETING_CARD_PRICE = from(3);

export interface BundleSkuById {
  [parentId: string]: string;
}

export interface CartLineItemFields {
  readonly autoDeliveryInterval?: number;
  readonly autoDeliveryOfferLocation?: string;
  readonly autoDeliveryOfferType?: string;
  readonly customizationsDescription?: string;
  readonly externalId?: string;
  readonly markedAsGift?: boolean;
  readonly occasionImageUrl?: string;
  readonly occasionName?: string;
  readonly parentExternalId?: string;
  /** stringified @type {DigitalGiftRecipient[]} */
  readonly recipientsJson?: string;
  readonly sendAt?: DateString;
  readonly senderName?: string;
}

type ChildLineItemCustomFields = SetRequired<CartLineItemFields, 'externalId' | 'parentExternalId'>;
type ParentLineItemCustomFields = Except<ChildLineItemCustomFields, 'parentExternalId'>;

const CUSTOM_PRODUCT_KEYS = [
  'Custom Mix',
  'Custom Tray',
  'Dynamic Bundle',
  'Gift Certificate',
  'Greeting Card',
] as const;

export type CustomProductKey = (typeof CUSTOM_PRODUCT_KEYS)[number];
export interface GreetingCardCartLineItemFields extends CartLineItemFields {
  readonly greetingCardMessage: string;
}
export interface PackingSlipMessageCustomLineItemFields {
  readonly packingSlipMessage: string;
}

export function isCustomProductKey(value?: string): value is CustomProductKey {
  return CUSTOM_PRODUCT_KEYS.some((key) => key === value);
}

export interface NutsLineItem<T extends CartLineItemFields = CartLineItemFields> extends LineItem {
  readonly children?: NutsLineItem[];
  readonly custom?: CustomFields & {
    readonly fields: FieldContainer & T;
  };
  readonly customizationsDescription?: string;
  readonly customProduct?: CustomProductKey;
  readonly piecePrice: Money;
  readonly totalSavings?: LineSavingsSummary;
  readonly productKey: string;
  readonly productPath?: string;
  readonly tagReferences?: CustomObjectReference[];
  readonly titleImage?: {
    readonly [size in keyof ImageBySize]: ImageBySize[size];
  };
  readonly totalPriceBeforeCartLevelDiscount: Money;
  readonly variant: ProductVariant & {
    readonly fixedAddress?: string;
    readonly pieceCost?: Money;
    readonly sku: string;
  } & Readonly<NutsVariantAttributes>;
}

export interface LineItemValidationResult {
  isValid: boolean;
  lineItem: NutsLineItem;
  metadata?: {
    ingredientNames?: string[];
  };
}

export type ProductValidatorFunction = (
  lineItem: NutsLineItem,
  allLineItems: NutsLineItem[],
) => LineItemValidationResult;

export function buildLineSavingsSummary({
  lineItemMode,
  price,
  priceMode,
  quantity,
  variant,
}: LineItem) {
  const pieceComparisonPrice = getListPrice(variant.prices ?? []);
  if (isGiftLineItem({ lineItemMode }) || priceMode !== 'Platform' || !pieceComparisonPrice) {
    return undefined;
  }
  const totalComparisonPrice = Money.multiply(pieceComparisonPrice.value, quantity);
  const totalPriceBeforeCartLevelDiscount = Money.multiply(getMoneyValue(price), quantity);
  const breakdown: LineSavingsBreakdownItem[] = [];
  let balance = totalComparisonPrice;

  const recordBreakdownItem = (
    type: LineSavingsBreakdownItem['type'],
    newPiecePrice: Money,
    description?: LocalizedString,
    onSale = false,
  ) => {
    const newTotalPrice = Money.multiply(newPiecePrice, quantity);
    breakdown.push({
      type,
      ...computeSavings(balance, newTotalPrice),
      description,
      onSale,
    });
    balance = newTotalPrice;
  };

  if (price.channel) {
    const channelPrice = variant.prices?.find((p) => p.channel?.id === price.channel!.id);
    // We don't currently expand price.channel except on receipt page, so check
    // variant.prices[*].channel too if needed.
    const channel =
      price.channel.obj ??
      variant.prices?.find((p) => p.channel?.obj?.id === price.channel!.id)?.channel?.obj;
    if (channelPrice) {
      const { discountPercent } = channel?.custom?.fields ?? {};
      recordBreakdownItem('Channel', channelPrice.value, {
        en: discountPercent ? `${discountPercent}% off Auto-Delivery` : 'Auto-Delivery',
      });
    }
  }
  if (price.discounted) {
    recordBreakdownItem(
      'ProductDiscount',
      price.discounted.value,
      price.discounted.discount?.obj?.description ?? { en: 'Sale Discount' },
      true,
    );
  } else if (price.tiers) {
    const tier = price.tiers?.find((t) => Money.equals(price.value, t.value));
    if (tier) {
      recordBreakdownItem('PriceTier', tier.value, { en: 'Bulk Discount' });
    }
  }
  if (!Money.equals(balance, totalPriceBeforeCartLevelDiscount)) {
    reportError(`balance != totalPriceBeforeCartLevelDiscount`);
  }
  return breakdown.length
    ? <LineSavingsSummary>{
        ...computeSavings(totalComparisonPrice, totalPriceBeforeCartLevelDiscount),
        breakdown,
        comparisonPrice: totalComparisonPrice,
        description: breakdown.find((b) => b.description)?.description,
        discountDisplayValue: getProductDiscountDisplayValueFromPrice(price, quantity),
        onSale: breakdown.some((b) => b.onSale),
      }
    : undefined;
}

export const NutsLineItem = {
  fromCt(lineItem: LineItem): NutsLineItem {
    const piecePrice = isGiftLineItem(lineItem) ? from(0) : getMoneyValue(lineItem.price);
    if (!lineItem.productKey) {
      if (import.meta.env.DEV) {
        throw new Error('productKey is missing');
      } else {
        reportError('productKey is missing');
      }
    }

    const pivotedAttributeValues = pivotAttributeValues(lineItem.variant.attributes);

    const potentialKey = pivotedAttributeValues.product.customProduct?.key;
    const customProduct = isCustomProductKey(potentialKey) ? potentialKey : undefined;

    if (potentialKey !== undefined && customProduct === undefined) {
      reportError(
        `Invalid customProduct.key encountered: "${potentialKey}". Expected one of: ${CUSTOM_PRODUCT_KEYS.join(
          ', ',
        )}.`,
      );
    }

    return {
      ...lineItem,
      customizationsDescription: lineItem.custom?.fields.customizationsDescription,
      customProduct,
      piecePrice,
      // We are confident that `lineItem.productKey` will be populated despite
      // being optional in CT's declaration. In our project it's present for all
      // (active) Carts, and missing only for Orders placed before Dec 4, 2021,
      // which we currently never access. See {@link LineItem.productKey} for
      // CT's detailed explanation.
      productKey: lineItem.productKey!,
      tagReferences: pivotedAttributeValues.product.tagReferences,
      totalPriceBeforeCartLevelDiscount: Money.multiply(piecePrice, lineItem.quantity),
      totalSavings: buildLineSavingsSummary(lineItem),
      variant: {
        ...lineItem.variant,
        fixedAddress: findAttributeByName(lineItem.variant.attributes, 'fixedAddress'),
        pieceCost: findAttributeByName(lineItem.variant.attributes, 'pieceCost'),
        sku: lineItem.variant.sku ?? '',
        ...pivotedAttributeValues.variant,
      },
    };
  },
};

function withSummedChildren(parent: NutsLineItem, children: NutsLineItem[]): NutsLineItem {
  const all = [parent, ...children];
  const totalPriceBeforeCartLevelDiscount = Money.sumBy(
    all,
    (c) => c.totalPriceBeforeCartLevelDiscount,
  );
  let totalSavings: LineSavingsSummary | undefined;

  const childSavings = all.flatMap((c) => c.totalSavings ?? []);
  if (childSavings.length) {
    const comparisonPrice = Money.sumBy(
      all,
      (c) => c.totalSavings?.comparisonPrice ?? c.totalPriceBeforeCartLevelDiscount,
    );
    let balance = comparisonPrice;
    const breakdown = childSavings
      .flatMap((cs) => cs.breakdown)
      .map((b) => {
        const newPrice = Money.subtract(balance, b.value);
        const { percent } = computeSavings(balance, newPrice);
        balance = newPrice;
        return { ...b, percent };
      });
    if (!Money.equals(balance, totalPriceBeforeCartLevelDiscount)) {
      reportError('balance != totalPriceBeforeCartLevelDiscount (in withSummedChildren)');
    }
    totalSavings = {
      // TODO: understand if it is possible / needed to calculate `discountDisplayValue` for parent + children
      ...computeSavings(comparisonPrice, totalPriceBeforeCartLevelDiscount),
      breakdown,
      comparisonPrice,
      // the first we find will do for now, until we have a clear use case
      description: childSavings.find((cs) => cs.description)?.description,
      onSale: childSavings.some((cs) => cs.onSale),
    };
  }

  const childrenNames = children.map((e) => e.name.en);
  const counts: { [key: string]: number } = childrenNames.reduce<{ [key: string]: number }>(
    (c, e) => {
      const newCounts = { ...c };
      newCounts[e] = newCounts[e] ? newCounts[e] + 1 : 1;
      return newCounts;
    },
    {},
  );
  const customizationsDescription = Object.entries(counts)
    .map((a) => `${a[1]}x ${a[0]}`)
    .join(', ');

  return {
    ...parent,
    children,
    customizationsDescription,
    piecePrice: Money.sumBy(all, (c) => c.piecePrice),
    taxedPrice: parent.taxedPrice && {
      totalGross: Money.sumBy(all, (c) => c.taxedPrice?.totalGross),
      totalNet: Money.sumBy(all, (c) => c.taxedPrice?.totalNet),
      taxPortions: [],
    },
    totalPrice: Money.sumBy(all, (c) => c.totalPrice),
    totalPriceBeforeCartLevelDiscount,
    totalSavings,
  };
}

type BaseBundleLineItem = SetRequired<NutsLineItem<ParentLineItemCustomFields>, 'custom'>;
type ParentLineItem = BaseBundleLineItem & {
  custom: BaseBundleLineItem['custom'] & {
    fields: Except<BaseBundleLineItem['custom']['fields'], 'parentExternalId'>;
  };
};

function isBundleParent(
  lineItem: NutsLineItem,
): lineItem is ParentLineItem & { customProduct: 'Dynamic Bundle' } {
  if (!isDefined(lineItem.custom) || lineItem.customProduct !== 'Dynamic Bundle') return false;
  if (isDefined(lineItem.custom.fields.parentExternalId)) return false;
  return isDefined(lineItem.custom.fields.externalId);
}

function groupChildrenByParent(lineItems: NutsLineItem[]) {
  return groupBy(lineItems, (li) => li.custom?.fields.parentExternalId ?? 'parents');
}

export function bundleParentSkusById(lineItems: NutsLineItem[]): BundleSkuById {
  const { parents } = groupChildrenByParent(lineItems);
  const bundleParents = parents?.filter(isBundleParent) ?? [];

  return Object.fromEntries(
    bundleParents.map((parent) => [parent.custom.fields.externalId, parent.variant.sku]),
  );
}

export function collapseChildren(lineItems: NutsLineItem[]) {
  const { parents, ...childrenByParentId } = groupChildrenByParent(lineItems);

  return (parents ?? []).map((parent) => {
    const children = childrenByParentId[parent.custom?.fields.externalId ?? ''];
    return children ? withSummedChildren(parent, children) : parent;
  });
}

/** Find products explicitly marked as a gift, or belonging to a gifting category */
export function containsGiftPurchase(lineItems: NutsLineItem[]) {
  return lineItems.some((item) => {
    if (item.custom?.fields.markedAsGift) return true;
    return findAttributeByName(item.variant.attributes, 'merchandisingCategory')?.key === 'Gifts';
  });
}

interface ProductNamesByKey {
  [productKey: string]: string;
}
/**
 * Customizers' volumetric ingredients are stored in custom fields.
 * This is a helper function to facilitate their extraction.
 */
export function volumetricIngredients(lineItem: NutsLineItem): ProductNamesByKey | undefined {
  if (
    !lineItem.productKey ||
    !lineItem.custom?.fields ||
    ![LARGE_CUSTOM_TRAY_PRODUCT_KEY, MEDIUM_CUSTOM_TRAY_PRODUCT_KEY, MIX_PRODUCT_KEY].includes(
      lineItem.productKey,
    )
  ) {
    return undefined;
  }
  const customTrayIngredientCount = 5;
  const customMixIngredientCount = INGREDIENT_LIMITS.max;

  const skuToIngredientCount: { [key: string]: number } = {
    [LARGE_CUSTOM_TRAY_PRODUCT_KEY]: customTrayIngredientCount,
    [MEDIUM_CUSTOM_TRAY_PRODUCT_KEY]: customTrayIngredientCount,
    [MIX_PRODUCT_KEY]: customMixIngredientCount,
  };

  const ingredientCountToPrefix: { [key: number]: string } = {
    [customTrayIngredientCount]: 'customTraySection',
    [customMixIngredientCount]: 'customMixIngredient',
  };

  const contents: ProductNamesByKey = {};
  const targetIngredientCount = skuToIngredientCount[lineItem.productKey];
  const targetPrefix = ingredientCountToPrefix[targetIngredientCount];
  // custom mix ingredients have a zero-based index (e.g. customMixIngredient0ProductKey)
  const start = targetIngredientCount === customTrayIngredientCount ? 1 : 0;
  const end =
    targetIngredientCount === customTrayIngredientCount
      ? targetIngredientCount
      : targetIngredientCount - 1;
  for (let i = start; i <= end; i += 1) {
    const key = lineItem.custom?.fields[`${targetPrefix}${i}ProductKey`];
    if (key) {
      contents[key] = lineItem.custom?.fields[`${targetPrefix}${i}ProductName`];
    }
  }
  return contents;
}

export function filterByAutoDeliveryStatus(lineItems: NutsLineItem[], hasInterval?: boolean) {
  return lineItems.filter((lineItem) => {
    const interval = lineItem.custom?.fields.autoDeliveryInterval;
    return hasInterval ? !!interval : !interval;
  });
}

export function getAutoDeliveryInterval(
  lineItem: NutsLineItem,
  format: 'days' | 'weeks' = 'weeks',
) {
  const divisor = format === 'days' ? 1 : 7;
  const interval = lineItem.custom?.fields.autoDeliveryInterval;
  return interval ? interval / divisor : interval;
}

const findIngredientMatches = (lineItem: NutsLineItem, targetKeys: string[]) => {
  const ingredients = volumetricIngredients(lineItem) ?? {};
  const targetKeySet = new Set(targetKeys);

  const matchingKeys = Object.keys(ingredients).filter((key) => targetKeySet.has(key));
  const matchingNames = compact(matchingKeys.map((key) => ingredients[key]));

  return { keys: matchingKeys, names: matchingNames };
};

export const validateBaseProduct: ProductValidatorFunction = (lineItem) => {
  const isValid = !lineItem.variant.backordered && lineItem.variant.active;
  return { isValid, lineItem };
};

export const validateBYOBChild: ProductValidatorFunction = (lineItem, parentLineItems) => {
  const parentExternalId = lineItem.custom?.fields.parentExternalId;
  if (!parentExternalId) {
    return { isValid: true, lineItem };
  }

  const parent = parentLineItems.find((li) => li.custom?.fields.externalId === parentExternalId);

  if (!parent || parent.productKey !== BYOB_PRODUCT_KEY) {
    return { isValid: true, lineItem };
  }

  if (!isBYOB(lineItem.variant)) {
    return {
      isValid: false,
      lineItem,
    };
  }

  return { isValid: true, lineItem };
};

export const createCustomizerContentValidator =
  (
    nonPurchasableContentKeys: string[],
    nonPurchasableForMixesKeys: string[],
    nonPurchasableForTraysKeys: string[],
  ): ProductValidatorFunction =>
  (lineItem) => {
    const allIssues: string[] = [];
    const allIngredientNames: string[] = [];

    const addIssuesIfFound = (keys: string[]) => {
      const matches = findIngredientMatches(lineItem, keys);
      if (matches.keys.length > 0) {
        allIssues.push(...matches.keys);
        allIngredientNames.push(...matches.names);
      }
    };

    addIssuesIfFound(nonPurchasableContentKeys);

    if (lineItem.productKey === MIX_PRODUCT_KEY) {
      addIssuesIfFound(nonPurchasableForMixesKeys);
    } else if (
      [LARGE_CUSTOM_TRAY_PRODUCT_KEY, MEDIUM_CUSTOM_TRAY_PRODUCT_KEY].includes(lineItem.productKey)
    ) {
      addIssuesIfFound(nonPurchasableForTraysKeys);
    }

    if (allIssues.length > 0) {
      const uniqueIngredientNames = [...new Set(allIngredientNames)];

      return {
        isValid: false,
        lineItem,
        metadata: {
          ingredientNames: uniqueIngredientNames,
        },
      };
    }
    return { isValid: true, lineItem };
  };

export const validateLineItems = (
  lineItems: NutsLineItem[],
  parentLineItems: NutsLineItem[],
  validators: ProductValidatorFunction[],
): LineItemValidationResult[] =>
  lineItems
    .map((lineItem) => {
      let result: LineItemValidationResult = { isValid: true, lineItem };
      validators.some((validator) => {
        result = validator(lineItem, parentLineItems);
        return !result.isValid;
      });
      return result;
    })
    .filter((result) => !result.isValid);

export const hasBundleDiscountAttached = (lineItem: NutsLineItem, discountId: string) =>
  lineItem.discountedPricePerQuantity?.some(({ discountedPrice }) =>
    discountedPrice.includedDiscounts?.some((portion) => portion.discount.id === discountId),
  ) ?? false;
